import type { ListCategoryDoc } from '../categories/categories.type'
import type { BaseApiResponse } from '../shared/shared.type'
import {
  INTEREST_MESSAGE,
  INTEREST_ROUTE,
  INTEREST_STATUS,
} from './interests.constant'
import type {
  BuildInterestFormPayloadInput,
  InterestFilter,
  InterestFormPayload,
  InterestListData,
  ShoppingInterest,
  UpdateInterestPayload,
} from './interests.type'

/**
 * Narrows unknown API values before response-envelope fields are read.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Validates the authoritative lifecycle values returned by the API.
 */
const isInterestStatus = (
  value: unknown,
): value is ShoppingInterest['status'] =>
  value === INTEREST_STATUS.ACTIVE ||
  value === INTEREST_STATUS.DRAFT ||
  value === INTEREST_STATUS.ARCHIVED

/**
 * Validates populated category and Product Tag references used by the UI.
 */
const isPopulatedReference = (
  value: unknown,
): value is { _id: string; name: string } =>
  isRecord(value) &&
  typeof value._id === 'string' &&
  typeof value.name === 'string'

/**
 * Validates every field dereferenced by management and ordering pages.
 */
const isShoppingInterest = (value: unknown): value is ShoppingInterest =>
  isRecord(value) &&
  typeof value._id === 'string' &&
  typeof value.name === 'string' &&
  typeof value.description === 'string' &&
  typeof value.active === 'boolean' &&
  isInterestStatus(value.status) &&
  typeof value.displayOrder === 'number' &&
  Array.isArray(value.categories) &&
  value.categories.every(isPopulatedReference) &&
  Array.isArray(value.productTags) &&
  value.productTags.every(isPopulatedReference) &&
  typeof value.createdAt === 'string' &&
  typeof value.updatedAt === 'string'

/**
 * Validates the list envelope and every entity field used by the UI. This
 * prevents malformed pages from being cached as ShoppingInterest records.
 */
export const isInterestListEnvelope = (
  value: unknown,
): value is BaseApiResponse<InterestListData> => {
  if (!isRecord(value) || !isRecord(value.data)) return false
  return (
    typeof value.data.count === 'number' &&
    Array.isArray(value.data.interests) &&
    value.data.interests.every(isShoppingInterest)
  )
}

/**
 * Applies the API's deterministic list order, using the interest name when
 * two records temporarily share a display position.
 */
export const compareInterestsByDisplayOrder = (
  firstInterest: ShoppingInterest,
  secondInterest: ShoppingInterest,
): number => {
  const orderDifference =
    firstInterest.displayOrder - secondInterest.displayOrder
  return orderDifference !== 0
    ? orderDifference
    : firstInterest.name.localeCompare(secondInterest.name)
}

/**
 * Returns the complete order accepted by the reorder endpoint. Archived
 * interests retain history but must not participate in future picker orders.
 */
export const buildInterestOrder = (interests: ShoppingInterest[]): string[] =>
  interests
    .filter(({ status }) => status !== INTEREST_STATUS.ARCHIVED)
    .sort(compareInterestsByDisplayOrder)
    .map(({ _id }) => _id)

/**
 * Confirms a draft order is a complete permutation of the latest server IDs.
 * This rejects duplicate, missing, newly archived, and newly created records.
 */
export const isCompleteInterestOrder = ({
  draftOrder,
  serverOrder,
}: {
  draftOrder: string[]
  serverOrder: string[]
}): boolean => {
  if (draftOrder.length !== serverOrder.length) return false
  const draftInterestIds = new Set(draftOrder)
  return (
    draftInterestIds.size === draftOrder.length &&
    serverOrder.every((interestId) => draftInterestIds.has(interestId))
  )
}

/**
 * Computes the append position for a new or restored non-archived interest.
 * Historic Archived positions are intentionally ignored.
 */
export const getNextDisplayOrder = (interests: ShoppingInterest[]): number =>
  Math.max(
    0,
    ...interests
      .filter(({ status }) => status !== INTEREST_STATUS.ARCHIVED)
      .map(({ displayOrder }) => displayOrder),
  ) + 1

/**
 * Filters the management catalogue without mutating its server order.
 */
export const filterInterestsByStatus = (
  interests: ShoppingInterest[],
  selectedFilter: InterestFilter,
): ShoppingInterest[] =>
  interests.filter(
    ({ status }) => selectedFilter === 'all' || status === selectedFilter,
  )

/**
 * Restricts mappings to active top-level categories and applies catalogue
 * order. Subcategories are not represented by ListCategoryDoc and therefore
 * cannot be submitted accidentally.
 */
export const filterActiveCategories = (
  categories: ListCategoryDoc[],
): ListCategoryDoc[] =>
  categories
    .filter(({ isActive }) => isActive)
    .sort((firstCategory, secondCategory) => {
      const orderDifference = firstCategory.sortOrder - secondCategory.sortOrder
      return orderDifference !== 0
        ? orderDifference
        : firstCategory.name.localeCompare(secondCategory.name)
    })

/**
 * Shapes form values into the supported write contract. Inactive IDs, legacy
 * active, and Product Tag fields cannot enter the returned payload.
 */
export const buildInterestFormPayload = ({
  values,
  activeCategoryIds,
}: BuildInterestFormPayloadInput): InterestFormPayload => ({
  name: values.name.trim(),
  description: values.description.trim(),
  status: values.status,
  categoryIds: values.categories
    .map(({ _id }) => _id)
    .filter((categoryId) => activeCategoryIds.has(categoryId)),
})

/**
 * Produces the only supported removal transition, retaining mappings and
 * history rather than deleting the record.
 */
export const buildArchiveInterestPayload = (): UpdateInterestPayload => ({
  status: INTEREST_STATUS.ARCHIVED,
})

/**
 * Restores an Archived interest as Draft at the end of the current
 * non-archived order.
 */
export const buildRestoreInterestPayload = (
  interests: ShoppingInterest[],
): UpdateInterestPayload => ({
  status: INTEREST_STATUS.DRAFT,
  displayOrder: getNextDisplayOrder(interests),
})

/**
 * Builds a concrete edit URL while keeping the route pattern centralized.
 */
export const getInterestEditRoute = (interestId: string): string =>
  `${INTEREST_ROUTE.LIST}/${interestId}/edit`

/**
 * Extracts either supported API failure message without exposing unknown
 * low-level errors to administrators.
 */
export const getInterestErrorMessage = (error: unknown): string => {
  if (!isRecord(error)) return INTEREST_MESSAGE.GENERIC_ERROR

  const directData = isRecord(error.data) ? error.data : undefined
  const nestedError = isRecord(error.error) ? error.error : undefined
  const nestedData =
    nestedError !== undefined && isRecord(nestedError.data)
      ? nestedError.data
      : undefined
  const errorData = directData ?? nestedData
  if (errorData === undefined) return INTEREST_MESSAGE.GENERIC_ERROR

  if (typeof errorData.error === 'string' && errorData.error !== '') {
    return errorData.error
  }
  return typeof errorData.message === 'string' && errorData.message !== ''
    ? errorData.message
    : INTEREST_MESSAGE.GENERIC_ERROR
}
